'use client';

import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import TypewriterText from '@/components/ui/TypewriterText';
import { Download, ArrowRight, Sparkles } from 'lucide-react';
import { FaGithub, FaLinkedinIn, FaTwitter, FaInstagram } from 'react-icons/fa';
import Link from 'next/link';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

const ParticleBackground = dynamic(
  () => import('@/components/ui/ParticleBackground'),
  { ssr: false }
);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: 'easeOut' as const },
  }),
};

const socials = [
  { icon: FaGithub, href: 'https://github.com/nasifniloy', label: 'GitHub', color: '#333' },
  { icon: FaLinkedinIn, href: 'https://linkedin.com/in/nasifniloy', label: 'LinkedIn', color: '#0A66C2' },
  { icon: FaTwitter, href: 'https://twitter.com/nasifniloy', label: 'Twitter', color: '#1DA1F2' },
  { icon: FaInstagram, href: 'https://instagram.com/nasifniloy', label: 'Instagram', color: '#E4405F' },
];

type HeroStat = { value: string; label: string };

type DynamicStat = {
  slug: string;
  label: string;
  icon: string;
  value: string;
  suffix: string;
  order: number;
};

type HeroData = {
  greeting: string;
  name: string;
  roles: string[];
  bio: string;
  imageUrl: string | null;
  ctaPrimaryText: string;
  ctaPrimaryLink: string;
  ctaSecondaryText: string;
  ctaSecondaryLink: string;
  badgeText: string;
  showStats: boolean;
  stats: HeroStat[];
};

const DEFAULTS: HeroData = {
  greeting: '',
  name: '',
  roles: [],
  bio: '',
  imageUrl: null,
  ctaPrimaryText: 'Download CV',
  ctaPrimaryLink: '/api/cv/download',
  ctaSecondaryText: 'Hire Me',
  ctaSecondaryLink: '/appointment',
  badgeText: '',
  showStats: true,
  stats: [],
};

function splitName(fullName: string): { first: string; rest: string } {
  const trimmed = (fullName || '').trim();
  if (!trimmed) return { first: '', rest: '' };
  const parts = trimmed.split(/\s+/);
  const first = parts.shift() || '';
  return { first, rest: parts.join(' ') };
}

export default function Hero({ initialData }: { initialData?: Partial<HeroData> } = {}) {
  const mergedDefaults: HeroData = initialData
    ? { ...DEFAULTS, ...initialData }
    : DEFAULTS;
  const data = useSiteData<HeroData>('hero', '/api/hero', mergedDefaults);
  const dynamicStats = useSiteData<DynamicStat[]>('stats', '/api/stats', []);

  // StatCounter table is the single source of truth. No legacy fallback —
  // if the admin hid every stat, render nothing (handled by the gate below).
  const resolvedStats: HeroStat[] = (dynamicStats || []).map((s) => ({
    value: `${s.value}${s.suffix || ''}`,
    label: s.label,
  }));

  const { first: firstName, rest: restOfName } = splitName(data.name);
  const avatarLetter = (firstName[0] || data.name[0] || 'N').toUpperCase();
  const isPrimaryDownload = /\/api\/cv\/download/.test(data.ctaPrimaryLink);

  return (
    <section className="hero-section">
      <ParticleBackground />

      {/* Gradient blobs */}
      <div className="hero-blob hero-blob-1" />
      <div className="hero-blob hero-blob-2" />

      <div className="hero-container">
        <div className="hero-grid">
          {/* Left Content */}
          <div className="hero-content">
            <motion.div
              className="hero-badge"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              <Sparkles size={14} />
              <span>{data.badgeText}</span>
            </motion.div>

            <motion.h1
              className="hero-title"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              {data.greeting}{' '}
              <span className="gradient-text">{firstName}</span>
              {restOfName ? (
                <>
                  <br />
                  <span className="hero-title-sub">{restOfName}</span>
                </>
              ) : null}
            </motion.h1>

            <motion.div
              className="hero-typewriter"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              <span className="hero-typewriter-prefix">I&apos;m a</span>
              {data.roles.length > 0 ? (
                <TypewriterText
                  texts={data.roles}
                  speed={80}
                  deleteSpeed={40}
                  pauseTime={2200}
                />
              ) : null}
            </motion.div>

            <motion.p
              className="hero-description"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              {data.bio}
            </motion.p>

            <motion.div
              className="hero-ctas"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
            >
              <Link href={data.ctaPrimaryLink} className="btn btn-primary">
                {isPrimaryDownload && <Download size={18} />}
                {data.ctaPrimaryText}
              </Link>
              <Link href={data.ctaSecondaryLink} className="btn btn-outline">
                {data.ctaSecondaryText}
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            {/* Stats */}
            {data.showStats && resolvedStats.length > 0 && (
              <motion.div
                className="hero-stats"
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={5}
              >
                {resolvedStats.map((stat, i) => (
                  <div key={`${stat.label}-${i}`} className="hero-stat">
                    <div className="hero-stat-value">{stat.value}</div>
                    <div className="hero-stat-label">{stat.label}</div>
                    {i < resolvedStats.length - 1 && <div className="hero-stat-divider" />}
                  </div>
                ))}
              </motion.div>
            )}

            <motion.div
              className="hero-socials"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={6}
            >
              <span className="hero-socials-label">Follow me</span>
              <div className="hero-socials-list">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="hero-social-btn"
                    style={{ '--hover-color': social.color } as React.CSSProperties}
                  >
                    <social.icon size={16} />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Content - Profile */}
          <motion.div
            className="hero-profile"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' as const }}
          >
            <div className="hero-profile-wrapper">
              {/* Rotating rings */}
              <div className="hero-ring hero-ring-1" />
              <div className="hero-ring hero-ring-2" />

              {/* Glow */}
              <div className="hero-profile-glow" />

              {/* Avatar */}
              <div className="hero-avatar">
                <div
                  className={`hero-avatar-inner ${
                    data.imageUrl ? '' : 'hero-avatar-inner--mono'
                  }`}
                >
                  {data.imageUrl ? (
                    <OptimizedImage
                      src={data.imageUrl}
                      alt={data.name}
                      className="hero-avatar-img"
                      fetchPriority="high"
                      loading="eager"
                      decoding="async"
                    />
                  ) : (
                    <span className="hero-avatar-text">{avatarLetter}</span>
                  )}
                </div>
              </div>

              {/* Floating cards */}
              <motion.div
                className="hero-float hero-float-1"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }}
              >
                <div className="hero-float-icon">⚛️</div>
                <div className="hero-float-text">
                  <div className="hero-float-title">React.js</div>
                  <div className="hero-float-sub">90% Expert</div>
                </div>
              </motion.div>

              <motion.div
                className="hero-float hero-float-2"
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' as const, delay: 1 }}
              >
                <div className="hero-float-icon">🚀</div>
                <div className="hero-float-text">
                  <div className="hero-float-title">Next.js 14</div>
                  <div className="hero-float-sub">88% Expert</div>
                </div>
              </motion.div>

              <motion.div
                className="hero-float hero-float-3"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' as const, delay: 0.5 }}
              >
                <span className="hero-float-badge-dot" />
                <span className="hero-float-badge-text">Available for Work</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="hero-scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <span>Scroll</span>
        <div className="hero-scroll-line">
          <motion.div
            className="hero-scroll-dot"
            animate={{ y: [0, 24, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' as const }}
          />
        </div>
      </motion.div>
    </section>
  );
}
